/**
 * Traffic Attribution Engine
 *
 * Captures and stores first-touch + last-touch attribution data for all visitors.
 * Identifies AI referral sources (ChatGPT, Perplexity, Claude, Gemini, Copilot).
 * Persists to localStorage for cross-page attribution and passes data to signup flow.
 *
 * Uses Vercel Analytics custom events for aggregated reporting.
 *
 * Data captured per visit:
 * - Referrer URL (document.referrer)
 * - UTM parameters (source, medium, campaign, content, term)
 * - Landing page URL + path
 * - AI source identification
 * - Timestamp + session tracking
 * - Device type (mobile/desktop/tablet)
 */

import { track } from '@vercel/analytics';

// =====================================================
// CONSTANTS
// =====================================================

const STORAGE_PREFIX = 'cmg_attr_';
const FIRST_TOUCH_KEY = `${STORAGE_PREFIX}first_touch`;
const LAST_TOUCH_KEY = `${STORAGE_PREFIX}last_touch`;
const SESSION_KEY = `${STORAGE_PREFIX}session_id`;
const VISIT_COUNT_KEY = `${STORAGE_PREFIX}visit_count`;

/** Session timeout: 30 minutes of inactivity = new session */
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

// =====================================================
// AI SOURCE DETECTION
// =====================================================

/**
 * Known AI referral source patterns.
 * Maps referrer domains and UTM sources to human-readable AI source names.
 *
 * Exported for `./channels.ts` (tracking v3 channel taxonomy) to detect the
 * 'AI' channel without duplicating this pattern list — see that file's
 * `classifyChannel()`.
 */
export const AI_SOURCE_PATTERNS: Array<{
  /** Pattern to match against referrer hostname or utm_source */
  pattern: RegExp;
  /** Human-readable source name */
  name: string;
  /** Category for grouping */
  category: 'ai-search' | 'ai-chat' | 'ai-assistant';
}> = [
  // ChatGPT (OpenAI) — the main one you're seeing
  { pattern: /chatgpt\.com/i, name: 'ChatGPT', category: 'ai-search' },
  { pattern: /chat\.openai\.com/i, name: 'ChatGPT', category: 'ai-search' },

  // Perplexity AI
  { pattern: /perplexity\.ai/i, name: 'Perplexity', category: 'ai-search' },

  // Google Gemini
  { pattern: /gemini\.google\.com/i, name: 'Google Gemini', category: 'ai-chat' },
  { pattern: /bard\.google\.com/i, name: 'Google Bard', category: 'ai-chat' },

  // Microsoft Copilot
  { pattern: /copilot\.microsoft\.com/i, name: 'Microsoft Copilot', category: 'ai-assistant' },
  { pattern: /bing\.com\/chat/i, name: 'Bing Chat', category: 'ai-assistant' },

  // Claude (Anthropic)
  { pattern: /claude\.ai/i, name: 'Claude', category: 'ai-chat' },

  // You.com AI
  { pattern: /you\.com/i, name: 'You.com', category: 'ai-search' },

  // Phind (developer AI search)
  { pattern: /phind\.com/i, name: 'Phind', category: 'ai-search' },

  // Kagi AI
  { pattern: /kagi\.com/i, name: 'Kagi', category: 'ai-search' },
];

// =====================================================
// TYPES
// =====================================================

export interface UTMParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
}

export interface AISourceMatch {
  name: string;
  category: 'ai-search' | 'ai-chat' | 'ai-assistant';
  /** Whether matched via referrer or UTM */
  matchedVia: 'referrer' | 'utm_source';
}

export interface TouchPoint {
  /** ISO timestamp */
  timestamp: string;
  /** Raw referrer URL */
  referrer: string;
  /** Referrer hostname extracted */
  referrerHost: string;
  /** UTM parameters */
  utm: UTMParams;
  /** Landing page path */
  landingPage: string;
  /** Full landing URL with query params */
  landingUrl: string;
  /** Detected AI source, if any */
  aiSource: AISourceMatch | null;
  /** Broad traffic channel */
  channel: TrafficChannel;
  /** Session ID */
  sessionId: string;
  /** Device type */
  device: 'mobile' | 'tablet' | 'desktop';
}

export type TrafficChannel =
  | 'ai-referral' // ChatGPT, Perplexity, etc.
  | 'organic-search' // Google, Bing, DuckDuckGo
  | 'social' // Facebook, Twitter, LinkedIn
  | 'referral' // Other websites
  | 'direct' // No referrer
  | 'email' // utm_medium=email
  | 'paid' // utm_medium=cpc/ppc/paid
  | 'unknown';

// =====================================================
// CORE FUNCTIONS
// =====================================================

/**
 * Extract UTM parameters from current URL
 */
function extractUTMParams(): UTMParams {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
  };
}

/** Auth-sensitive params that must never be persisted in attribution data */
const SENSITIVE_PARAMS = ['code', 'token', 'access_token', 'refresh_token', 'code_verifier'];

function stripSensitiveParams(href: string): string {
  try {
    const url = new URL(href);
    let stripped = false;
    for (const param of SENSITIVE_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        stripped = true;
      }
    }
    return stripped ? url.toString() : href;
  } catch (_e) {
    // intentional: invalid URL returns original string
    return href;
  }
}

/**
 * Extract hostname from a URL string safely
 */
function extractHostname(url: string): string {
  if (!url) return '';
  try {
    return new URL(url).hostname;
  } catch (_e) {
    // intentional: invalid URL returns empty hostname
    return '';
  }
}

/**
 * Detect device type from user agent
 */
function detectDevice(): 'mobile' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android.*mobile|windows phone|blackberry/i.test(ua)) return 'mobile';
  return 'desktop';
}

/**
 * Identify AI source from referrer URL and UTM params
 */
function identifyAISource(referrer: string, utm: UTMParams): AISourceMatch | null {
  const referrerHost = extractHostname(referrer);

  // Check referrer hostname against known AI patterns
  for (const source of AI_SOURCE_PATTERNS) {
    if (referrerHost && source.pattern.test(referrerHost)) {
      return {
        name: source.name,
        category: source.category,
        matchedVia: 'referrer',
      };
    }
  }

  // Check utm_source against known AI patterns
  if (utm.utm_source) {
    for (const source of AI_SOURCE_PATTERNS) {
      if (source.pattern.test(utm.utm_source)) {
        return {
          name: source.name,
          category: source.category,
          matchedVia: 'utm_source',
        };
      }
    }
  }

  return null;
}

/**
 * Classify traffic into a broad channel
 */
function classifyChannel(
  referrer: string,
  utm: UTMParams,
  aiSource: AISourceMatch | null
): TrafficChannel {
  // AI referral takes priority
  if (aiSource) return 'ai-referral';

  // UTM-based classification
  if (utm.utm_medium) {
    const medium = utm.utm_medium.toLowerCase();
    if (medium === 'email' || medium === 'newsletter') return 'email';
    if (['cpc', 'ppc', 'paid', 'paidsocial', 'paid_social', 'display'].includes(medium))
      return 'paid';
    if (['social', 'social-media', 'sm'].includes(medium)) return 'social';
    if (medium === 'organic') return 'organic-search';
  }

  // Referrer-based classification
  const host = extractHostname(referrer);
  if (!host) return 'direct';

  // Search engines
  if (/google\.|bing\.|yahoo\.|duckduckgo\.|baidu\.|yandex\./i.test(host)) return 'organic-search';

  // Social media
  if (
    /facebook\.|fb\.|instagram\.|twitter\.|x\.com|linkedin\.|tiktok\.|pinterest\.|reddit\./i.test(
      host
    )
  )
    return 'social';

  // Anything else with a referrer
  return 'referral';
}

/**
 * Get or create a session ID.
 * Sessions expire after 30 minutes of inactivity.
 */
function getSessionId(): string {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      const { id, lastActivity } = JSON.parse(stored);
      const now = Date.now();
      if (now - lastActivity < SESSION_TIMEOUT_MS) {
        // Update last activity
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastActivity: now }));
        return id;
      }
    }
  } catch (_e) {
    // intentional: sessionStorage may be unavailable in SSR/iframe
  }

  // Create new session
  const id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastActivity: Date.now() }));
  } catch (_e) {
    // intentional: sessionStorage write is fire-and-forget
  }
  return id;
}

/**
 * Build a TouchPoint from current page context
 */
function buildTouchPoint(): TouchPoint {
  const referrer = document.referrer || '';
  const utm = extractUTMParams();
  const aiSource = identifyAISource(referrer, utm);
  const channel = classifyChannel(referrer, utm, aiSource);

  return {
    timestamp: new Date().toISOString(),
    referrer,
    referrerHost: extractHostname(referrer),
    utm,
    landingPage: window.location.pathname,
    landingUrl: stripSensitiveParams(window.location.href),
    aiSource,
    channel,
    sessionId: getSessionId(),
    device: detectDevice(),
  };
}

// =====================================================
// STORAGE
// =====================================================

/**
 * Store first-touch attribution (only if not already set)
 */
function storeFirstTouch(touchPoint: TouchPoint): void {
  try {
    const existing = localStorage.getItem(FIRST_TOUCH_KEY);
    if (!existing) {
      localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touchPoint));
    }
  } catch (_e) {
    // intentional: localStorage may be unavailable in SSR/iframe
  }
}

/**
 * Store last-touch attribution (always overwrites)
 */
function storeLastTouch(touchPoint: TouchPoint): void {
  try {
    localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(touchPoint));
  } catch (_e) {
    // intentional: localStorage may be unavailable in SSR/iframe
  }
}

/**
 * Increment visit count
 */
function incrementVisitCount(): number {
  try {
    const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
    const newCount = (Number.isNaN(count) ? 0 : count) + 1;
    localStorage.setItem(VISIT_COUNT_KEY, newCount.toString());
    return newCount;
  } catch (_e) {
    // intentional: localStorage unavailable, return default count
    return 1;
  }
}

// =====================================================
// VERCEL ANALYTICS EVENTS
// =====================================================

/**
 * Fire Vercel Analytics custom events for this visit.
 * These appear in Vercel dashboard → Analytics → Custom Events.
 */
function fireAnalyticsEvents(touchPoint: TouchPoint, visitCount: number): void {
  // Event 1: Every page visit with attribution
  track('page_visit', {
    channel: touchPoint.channel,
    referrerHost: touchPoint.referrerHost || 'direct',
    landingPage: touchPoint.landingPage,
    device: touchPoint.device,
    visitNumber: visitCount,
    ...(touchPoint.utm.utm_source && { utmSource: touchPoint.utm.utm_source }),
    ...(touchPoint.utm.utm_medium && { utmMedium: touchPoint.utm.utm_medium }),
    ...(touchPoint.utm.utm_campaign && { utmCampaign: touchPoint.utm.utm_campaign }),
  });

  // Event 2: AI-specific event (only fires for AI referrals)
  if (touchPoint.aiSource) {
    track('ai_referral', {
      aiSource: touchPoint.aiSource.name,
      aiCategory: touchPoint.aiSource.category,
      matchedVia: touchPoint.aiSource.matchedVia,
      landingPage: touchPoint.landingPage,
      device: touchPoint.device,
      ...(touchPoint.utm.utm_source && { utmSource: touchPoint.utm.utm_source }),
    });
  }
}

// =====================================================
// PUBLIC API
// =====================================================

/**
 * Main entry point: capture and store traffic attribution for this visit.
 * Call once per page load on landing/marketing pages.
 *
 * - Builds touch point from referrer + UTMs + AI detection
 * - Stores first-touch (once) and last-touch (always)
 * - Fires Vercel Analytics custom events
 * - Returns the touch point for optional further use
 */
export function captureTrafficAttribution(): TouchPoint {
  const touchPoint = buildTouchPoint();
  const visitCount = incrementVisitCount();

  // Store attribution
  storeFirstTouch(touchPoint);
  storeLastTouch(touchPoint);

  // Fire analytics
  fireAnalyticsEvents(touchPoint, visitCount);

  return touchPoint;
}

/**
 * Get stored first-touch attribution (for passing to signup/checkout)
 */
export function getFirstTouchAttribution(): TouchPoint | null {
  try {
    const stored = localStorage.getItem(FIRST_TOUCH_KEY);
    return stored ? (JSON.parse(stored) as TouchPoint) : null;
  } catch (_e) {
    // intentional: localStorage/JSON parse may fail in SSR/iframe
    return null;
  }
}

/**
 * Get stored last-touch attribution
 */
export function getLastTouchAttribution(): TouchPoint | null {
  try {
    const stored = localStorage.getItem(LAST_TOUCH_KEY);
    return stored ? (JSON.parse(stored) as TouchPoint) : null;
  } catch (_e) {
    // intentional: localStorage/JSON parse may fail in SSR/iframe
    return null;
  }
}

/**
 * Get attribution summary for passing to signup form / checkout.
 * Returns a flat object suitable for storing in user metadata.
 */
export function getAttributionSummary(): Record<string, string | null> {
  const first = getFirstTouchAttribution();
  const last = getLastTouchAttribution();
  const visitCount = localStorage.getItem(VISIT_COUNT_KEY) || '1';

  return {
    // First touch (how they originally found you)
    first_touch_source:
      first?.aiSource?.name || first?.utm.utm_source || first?.referrerHost || 'direct',
    first_touch_channel: first?.channel || 'direct',
    first_touch_landing: first?.landingPage || '/',
    first_touch_date: first?.timestamp || null,

    // Last touch (what brought them back this time)
    last_touch_source:
      last?.aiSource?.name || last?.utm.utm_source || last?.referrerHost || 'direct',
    last_touch_channel: last?.channel || 'direct',

    // Engagement
    total_visits: visitCount,

    // AI-specific (the data you care about most)
    ai_source: first?.aiSource?.name || last?.aiSource?.name || null,
    ai_category: first?.aiSource?.category || last?.aiSource?.category || null,
  };
}

/**
 * Clear all attribution data (call after successful conversion/signup)
 */
export function clearAttribution(): void {
  try {
    localStorage.removeItem(FIRST_TOUCH_KEY);
    localStorage.removeItem(LAST_TOUCH_KEY);
    localStorage.removeItem(VISIT_COUNT_KEY);
  } catch {
    // Fire-and-forget
  }
}
